import { useState } from 'react';
import { concat, entries, groupBy, values } from 'lodash';
import {
  Badge,
  Collapse,
  Flex,
  Icon,
  Input,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { allIcons } from '../utils/Icon';
import { useSelector } from 'react-redux';
import { TState } from '../../redux/store';
import { isEmpty } from '../../helpers';
import { momentTimezone } from '../utils/dateUtils';

const translate: Record<string, string> = {
  documentId: 'Documents',
};

const CollapseTd = ({
  children,
  collapsed,
}: {
  children?: any;
  collapsed?: boolean;
}) => {
  return (
    <Td
      padding={isEmpty(collapsed) || collapsed ? 2 : 0}
      border={0}
      transition='padding .2s'
    >
      <Collapse in={collapsed ?? true} animate={false}>
        {children || ''}
      </Collapse>
    </Td>
  );
};

const Row = ({
  d,
  onclick,
  collapsed,
}: {
  d: any;
  onclick?: any;
  collapsed?: boolean;
}) => {
  const theme = useSelector((state: TState) => state.theme);

  return (
    <Tr
      height={0}
      whiteSpace='nowrap'
      onClick={onclick}
      bg={onclick ? theme.light : ''}
      cursor={onclick ? 'pointer' : 'auto'}
    >
      <CollapseTd collapsed={collapsed}>
        <Icon
          as={
            allIcons[
              d.isDocument
                ? 'HiDocumentText'
                : d.isPlanning
                  ? 'HiCalendarDays'
                  : 'HiFolderOpen'
            ]
          }
        />
      </CollapseTd>
      <CollapseTd collapsed={collapsed}>
        {momentTimezone(d.date).format('DD/MM/YYYY HH:mm')}
      </CollapseTd>
      <CollapseTd collapsed={collapsed}>{d.action}</CollapseTd>
      <CollapseTd collapsed={collapsed}>{d.user}</CollapseTd>
      {onclick && (
        <>
          <Td />
          <Td />
          <Td />
        </>
      )}
      {!onclick && (
        <>
          <CollapseTd collapsed={collapsed}>
            {translate[`${d.key}`] || d.key}
          </CollapseTd>
          <CollapseTd collapsed={collapsed}>
            <Badge
              maxW='30vw'
              overflow='hidden'
              textOverflow='ellipsis'
              borderRadius={5}
              backgroundColor='red.100'
              textTransform='none'
            >
              {(/string|number/.test(typeof d.oldValue)
                ? d.oldValue
                : d.oldValue?.value) ?? '-'}
            </Badge>
          </CollapseTd>
          <CollapseTd collapsed={collapsed}>
            <Badge
              maxW='30vw'
              overflow='hidden'
              textOverflow='ellipsis'
              borderRadius={5}
              backgroundColor='green.100'
              textTransform='none'
            >
              {(/string|number/.test(typeof d.newValue)
                ? d.newValue
                : d.newValue?.value) ?? '-'}
            </Badge>
          </CollapseTd>
        </>
      )}
    </Tr>
  );
};

const CollapsedRows = ({ datas }: { datas: any[] }) => {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <>
      <Row d={datas[0]} onclick={() => setCollapsed((c) => !c)} />
      {datas.map((d) => (
        <Row d={d} collapsed={collapsed} />
      ))}
    </>
  );
};

const HistoryTable = ({ data }: any) => {
  const [historyFilter, setHistoryFilter] = useState('');

  const reducedData = data
    ?.reduce(
      (prev: any, { diff, ...d }: any) =>
        concat(
          prev,
          entries(diff).flatMap(([k, [oldValue, newValue]]: any) => ({
            ...d,
            key: k,
            action:
              d.action ||
              (d.isDelete
                ? 'Supprimé'
                : d.isDocument
                  ? 'Ajouté'
                  : d.isCreate
                    ? 'Créé'
                    : 'Modifié'),
            oldValue,
            newValue,
          })),
        ),
      [],
    )
    .reverse();

  const filteredData =
    reducedData?.filter((rd: any) =>
      values(rd).join(' ').toLowerCase().includes(historyFilter.toLowerCase()),
    ) || [];

  const groupedData = groupBy(reducedData, '_id');

  return (
    <>
      <Input
        placeholder='Rechercher un champ'
        mb={2}
        onChange={(e) => setHistoryFilter(e.target.value)}
        value={historyFilter}
      />
      <Table size='sm' layout='fixed'>
        <Thead>
          <Tr>
            <Th flexDirection='column' w={50} pl={2}>
              <Flex>
                <Icon as={allIcons.HiFolderOpen} />
                <Icon as={allIcons.HiCalendarDays} />
                <Icon as={allIcons.HiDocumentText} />
              </Flex>
            </Th>
            <Th pl={2} w='130px'>
              Date
            </Th>
            <Th pl={2} w='100px'>
              Action
            </Th>
            <Th pl={2} w='150px'>
              Par
            </Th>
            <Th pl={2} w='200px'>
              Nom
            </Th>
            <Th pl={2} w={['200px', '200px', 'auto']}>
              Avant
            </Th>
            <Th pl={2} w={['200px', '200px', 'auto']}>
              Après
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {historyFilter && filteredData.map((d: any) => <Row d={d} />)}
          {!historyFilter && (
            <>
              {values(groupedData).map((data) => (
                <CollapsedRows datas={data} />
              ))}
            </>
          )}
        </Tbody>
      </Table>
    </>
  );
};

export default HistoryTable;
